import { Box, List, useMediaQuery, useTheme } from "@mui/material";
import { useLocation } from "react-router-dom";
import NavItem from "./SidebarNavItem";

import { useAuth } from "@/shared/stores/useAuth";
import { useCurrentOrganization } from "@/modules/organization/useCurrentOrganization";
import { Apartment, CollectionsBookmark, Groups, Home as HomeIcon } from "@mui/icons-material";
import { Settings } from "lucide-react";
import {
  GoldSidebarDivider,
  SidebarShell,
  useCloseDrawerOnRouteChange,
} from "./sidebarDesign";
import { useTranslation } from "react-i18next";
import { ROUTES } from "@/shared/constants";
import { isNavItemSelected } from "@/shared/utils/isNavItemSelected";

type UserSidebarProps = {
  open: boolean;
  handleClose: () => void;
  toggleDrawer?: () => void;
  coffeeJoinChannel?: string | null;
  coffeeJoinMode?: string;
};

const UserSidebar = ({ open, handleClose, toggleDrawer }: UserSidebarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const { t } = useTranslation("sidebar");

  const { userInfo } = useAuth();
  const { isOrganizationWorkspace } = useCurrentOrganization();
  const isGuestLite = userInfo?.profile_completed === false;
  // Tooltip only when desktop rail is collapsed; layout is always full (shell clips).
  const showRailTooltip = !isMobile && !open;

  // Dismiss the temporary drawer (and its backdrop) whenever the route changes on
  // mobile, so navigating never leaves a lingering overlay blocking the page.
  useCloseDrawerOnRouteChange(isMobile, location.pathname, handleClose);

  const exploreItems = isOrganizationWorkspace
    ? [
        { text: t("lobby"), icon: <HomeIcon />, path: ROUTES.ORGANIZATION_LOBBY },
        { text: t("hall"), icon: <Apartment />, path: ROUTES.ORGANIZATION_HALL },
      ]
    : [
        { text: t("lobby"), icon: <HomeIcon />, path: ROUTES.LOBBY },
        { text: t("halls"), icon: <Groups />, path: ROUTES.TALENTS },
      ];

  const workspaceItems = [
    { text: t("charm"), icon: <CollectionsBookmark />, path: ROUTES.CHARM },
    // { text: t("inbox"), icon: <Mail size={24} />, path: ROUTES.INBOX },
    // ...(!isGuestLite
    //   ? [{ text: t("wallet"), icon: <Wallet size={24} />, path: ROUTES.WALLET }]
    //   : []),
  ];

  const bottomItems = [
    { text: t("settings"), icon: <Settings size={24} />, path: ROUTES.SETTINGS },
    // { text: t("help"), icon: <HelpCircle size={24} />, path: "/help" },
  ];

  const navOpen = !showRailTooltip;

  return (
    <SidebarShell
      open={open}
      isMobile={isMobile}
      onClose={handleClose}
      onToggle={toggleDrawer}
      body={
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 4.25,
            pt: 3.5,
            px: 0.25,
          }}
        >
          <Box>
            <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {[...exploreItems, ...workspaceItems].map((item) => (
                <NavItem
                  key={item.path}
                  variant="landing"
                  item={item}
                  open={navOpen}
                  selected={isNavItemSelected(location.pathname, item.path)}
                  onClick={isMobile ? handleClose : undefined}
                />
              ))}
            </List>
          </Box>
        </Box>
      }
      footer={
        <Box sx={{ mb: 2 }}>
          <GoldSidebarDivider width="85%" open={navOpen} />
          <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 1, px: 0.25 }}>
            {bottomItems.map((item) => (
              <NavItem
                key={item.path}
                variant="landing"
                item={item}
                open={navOpen}
                selected={isNavItemSelected(location.pathname, item.path)}
                onClick={isMobile ? handleClose : undefined}
              />
            ))}
          </List>
        </Box>
      }
    />
  );
};

export default UserSidebar;
